import { useEffect, useRef, useState, type RefObject } from "react";
import type { VoiceParticipant } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { getSocket } from "../../lib/socket";
import { Avatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Tooltip } from "../../components/ui/Tooltip";
import {
  IconEye,
  IconEyeOff,
  IconFocus,
  IconFullscreen,
  IconFullscreenExit,
  IconPip,
  IconPipExit,
  IconStreamMuted,
  IconStreamVolume,
  IconUnfocus,
} from "../../components/icons";
import { useStreamUi, useStreamVolume } from "./stores";
import { qualityLabel } from "./stream";
import { useVoice } from "./VoiceProvider";

/** "Live" marker for someone sharing their screen. Text, not just colour. */
export function LiveBadge({ className }: { className?: string }) {
  return <span className={cn("inline-flex h-4 shrink-0 items-center rounded-sm bg-danger px-1 text-[11px] font-semibold leading-none text-white", className)}>Live</span>;
}

export function StreamVideo({
  stream,
  muted,
  volume = 1,
  className,
  label,
  videoRef,
}: {
  stream: MediaStream;
  muted: boolean;
  volume?: number;
  className?: string;
  label: string;
  videoRef?: RefObject<HTMLVideoElement | null>;
}) {
  const own = useRef<HTMLVideoElement>(null);
  const ref = videoRef ?? own;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const attach = () => {
      el.srcObject = stream;
      void el.play().catch(() => {});
    };
    attach();
    stream.addEventListener("addtrack", attach);
    stream.addEventListener("removetrack", attach);
    return () => {
      stream.removeEventListener("addtrack", attach);
      stream.removeEventListener("removetrack", attach);
      el.srcObject = null;
    };
  }, [stream, ref]);
  useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume, ref]);
  return <video ref={ref} autoPlay playsInline muted={muted} aria-label={label} className={cn("size-full bg-black object-contain", className)} />;
}

/** The latest snapshot of a stream while you're not watching it. Polls quickly until the first one arrives. */
export function useStreamPreview(socketId: string, enabled: boolean) {
  const [image, setImage] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let got = false;
    let timer: ReturnType<typeof setTimeout>;
    const load = () => {
      getSocket()?.emit("voice:preview:get", { socketId }, (res) => {
        if (!alive) return;
        if (res.ok && res.data?.image) {
          got = true;
          setImage(res.data.image);
        }
      });
      timer = setTimeout(load, got ? 10_000 : 3_000);
    };
    load();
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [socketId, enabled]);
  return enabled ? image : null;
}

function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === ref.current && !!ref.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, [ref]);
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void ref.current?.requestFullscreen?.().catch(() => {});
  };
  return [full, toggle] as const;
}

export const pipSupported = () => typeof document !== "undefined" && !!document.pictureInPictureEnabled;

/** Pop the stream out into the browser's own floating window. */
export function usePictureInPicture(ref: RefObject<HTMLVideoElement | null>, stream: MediaStream | null | undefined) {
  const [pip, setPip] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const on = () => setPip(true);
    const off = () => setPip(false);
    el.addEventListener("enterpictureinpicture", on);
    el.addEventListener("leavepictureinpicture", off);
    return () => {
      el.removeEventListener("enterpictureinpicture", on);
      el.removeEventListener("leavepictureinpicture", off);
    };
  }, [ref, stream]);
  const toggle = () => {
    if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => {});
    else void ref.current?.requestPictureInPicture?.().catch(() => {});
  };
  return [pip, toggle] as const;
}

/** How many people are watching, with their names on hover. */
export function ViewerCount({ stream, participants, className }: { stream: VoiceParticipant; participants: VoiceParticipant[]; className?: string }) {
  const ids = new Set(stream.stream?.viewers ?? []);
  const viewers = participants.filter((p) => ids.has(p.socketId));
  if (!viewers.length) return null;
  const names = viewers.map((v) => v.user.displayName).join(", ");
  return (
    <Tooltip label={`Watching: ${names}`}>
      <span tabIndex={0} className={cn("pointer-events-auto inline-flex items-center gap-1 rounded-sm text-xs font-medium tabular outline-none", className)} aria-label={`${viewers.length} watching: ${names}`}>
        <IconEye className="icon size-3.5" aria-hidden />
        {viewers.length}
      </span>
    </Tooltip>
  );
}

const overlayButton = "text-white hover:bg-white/15 hover:text-white";

/** Mute and a volume slider for a stream's sound, remembered per streamer. */
export function StreamVolume({ p }: { p: VoiceParticipant }) {
  const { volume, muted } = useStreamVolume(p.user.id);
  const setVolume = useStreamUi((s) => s.setVolume);
  const toggleMuted = useStreamUi((s) => s.toggleMuted);
  const silent = muted || volume === 0;
  return (
    <div className="group/vol flex items-center">
      <IconButton size="sm" label={silent ? "Unmute stream" : "Mute stream"} icon={silent ? IconStreamMuted : IconStreamVolume} onClick={() => toggleMuted(p.user.id)} className={overlayButton} />
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={muted ? 0 : Math.round(volume * 100)}
        onChange={(e) => setVolume(p.user.id, Number(e.target.value) / 100)}
        aria-label={`Stream volume for ${p.user.displayName}`}
        className="h-8 w-0 cursor-pointer accent-white opacity-0 transition-[width,opacity] duration-150 focus:w-20 focus:opacity-100 group-hover/vol:w-20 group-hover/vol:opacity-100 motion-reduce:transition-none"
      />
    </div>
  );
}

/**
 * A screen share: the video with viewer controls when you're watching,
 * otherwise a live snapshot and a way to watch.
 */
export function StreamTile({
  p,
  participants,
  focused,
  className,
  small,
}: {
  p: VoiceParticipant;
  participants: VoiceParticipant[];
  focused: boolean;
  className?: string;
  /** In the strip under a focused stream: fewer controls. */
  small?: boolean;
}) {
  const voice = useVoice();
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [full, toggleFull] = useFullscreen(ref);
  const setFocused = useStreamUi((s) => s.setFocused);
  const hideSelf = useStreamUi((s) => s.hideSelfPreview);
  const setHideSelf = useStreamUi((s) => s.setHideSelfPreview);
  const vol = useStreamVolume(p.user.id);
  const self = p.socketId === voice.selfSocketId;
  const inRoom = voice.room?.id === p.channelId && voice.status === "connected";
  const watching = self || (inRoom && voice.watching.has(p.socketId));
  const stream = self ? voice.localScreen : voice.screens[p.socketId];
  const [pip, togglePip] = usePictureInPicture(videoRef, stream);
  const preview = useStreamPreview(p.socketId, !watching);
  const name = self ? "Your screen" : `${p.user.displayName}'s screen`;
  const showVideo = watching && stream && !(self && hideSelf);
  const info = p.stream;
  const focus = () => setFocused(focused ? null : p.socketId);

  const caption = (
    <div className={cn("pointer-events-none absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md bg-overlay px-2 py-1 text-xs font-medium text-white", watching ? "max-w-[55%]" : "max-w-[calc(100%-1rem)]")}>
      <LiveBadge />
      <span className="truncate">{self ? "You" : p.user.displayName}</span>
      {info && !small && <span className="hidden shrink-0 text-white/70 tabular group-hover:inline group-focus-within:inline">{qualityLabel(info)}</span>}
      <ViewerCount stream={p} participants={participants} className="ml-0.5 text-white/90" />
    </div>
  );

  return (
    <div
      ref={ref}
      className={cn("group relative flex aspect-video min-h-0 items-center justify-center overflow-hidden rounded-xl", showVideo ? "bg-black" : "bg-bg-subtle", className)}
      onDoubleClick={showVideo ? toggleFull : undefined}
    >
      {showVideo ? (
        <StreamVideo stream={stream} muted={self || voice.deafened || vol.muted} volume={vol.volume} label={name} videoRef={videoRef} />
      ) : watching && self && stream ? (
        <div className="flex flex-col items-center gap-3 p-4 text-center">
          <p className="text-sm text-fg-2">You're live{info ? ` · ${qualityLabel(info)}` : ""}</p>
          <Button size="sm" icon={IconEye} onClick={() => setHideSelf(false)}>
            Show preview
          </Button>
        </div>
      ) : watching ? (
        <div className="flex flex-col items-center gap-2 text-sm text-fg-3">
          <Spinner className="size-5" />
          {self ? "Starting your stream…" : "Connecting to the stream…"}
        </div>
      ) : (
        <>
          {preview && <img src={preview} alt={`Preview of ${name}`} className="absolute inset-0 size-full object-cover" draggable={false} />}
          <div className={cn("flex flex-col items-center gap-3 p-4 text-center", preview ? "absolute inset-0 justify-center bg-black/40" : "relative")}>
            {!preview && !small && <Avatar src={p.user.avatarUrl} name={p.user.displayName} size="xl" />}
            {inRoom ? (
              <Button variant="primary" size="sm" icon={IconEye} onClick={() => voice.setWatching(p.socketId, true)}>
                {small ? "Watch" : "Watch stream"}
              </Button>
            ) : (
              <p className={cn("text-sm", preview ? "text-white" : "text-fg-3")}>Join to watch</p>
            )}
          </div>
        </>
      )}
      {caption}
      {watching && (stream || self) && (
        <div className="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-md bg-overlay p-0.5 text-white opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          {!self && stream && (info?.audio || stream.getAudioTracks().length > 0) && !small && <StreamVolume p={p} />}
          {self && stream && !small && (
            <IconButton size="sm" label={hideSelf ? "Show preview" : "Hide preview"} icon={hideSelf ? IconEye : IconEyeOff} onClick={() => setHideSelf(!hideSelf)} className={overlayButton} />
          )}
          {showVideo && pipSupported() && !small && (
            <IconButton size="sm" label={pip ? "Exit picture in picture" : "Picture in picture"} icon={pip ? IconPipExit : IconPip} onClick={togglePip} className={overlayButton} />
          )}
          <IconButton size="sm" label={focused ? "Exit focus" : "Focus"} icon={focused ? IconUnfocus : IconFocus} onClick={focus} className={overlayButton} />
          {showVideo && !small && (
            <IconButton size="sm" label={full ? "Exit full screen" : "Full screen"} icon={full ? IconFullscreenExit : IconFullscreen} onClick={toggleFull} className={overlayButton} />
          )}
          {!self && <IconButton size="sm" label="Stop watching" icon={IconEyeOff} onClick={() => voice.setWatching(p.socketId, false)} className={overlayButton} />}
        </div>
      )}
    </div>
  );
}
